import { apiGet, APIURL, cancelSelectEvent, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getWidgetDataURI, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType, WinnersLosersExchangeConfig } from './../../types';

export class TraderverseWinnersLosersExchange {
  private container: HTMLElement;
  private config: WinnersLosersExchangeConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private shadowRoot: ShadowRoot;
  private widgetResizeObserver: ResizeObserver | null = null;
  private exchangeData: any = null;
  private isLoading: boolean = false;

  constructor(config: WinnersLosersExchangeConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private getDefaultConfig(): Partial<WidgetConfig> & { mover_type: string; interval: string } {
    return {
      headerPinned: true,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true,
      mover_type: 'MostActive',
      interval: '1d'
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.attachStyles();
    this.setupEventListeners();
    await this.fetchExchangeData();
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
    let dataTheme = 'default-dark';
    let widgetIndex = 0;
    
    if (this.config.theme === 'white') {
      themeClass = 'whiteMode';
      dataTheme = 'white';
    } else if (this.config.theme === 'black') {
      themeClass = 'blackMode';
      dataTheme = 'black';
    } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width};height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox traderverse-widget ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-testing-id="winners-losers-exchange-widget-${widgetIndex}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title market">${this.getTitleIconSvg()} Winners/Losers by Exchange</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)} <i class="pi pi-ellipsis-v"></i> </div>
        </div>
        <div class="inBody">
          <div class="optionsRow">
            <div class="item">
              <select class="exchange" id="exchange">
                <option selected value="all">all</option>
                <option value="AMEX">AMEX</option>
                <option value="BUE">BUE</option>
                <option value="CXI">CXI</option>
                <option value="DJI">DJI</option>
                <option value="ICEF">ICEF</option>
                <option value="MSC">MSC</option>
                <option value="NASDAQ">NASDAQ</option>
                <option value="NYSE">NYSE</option>
                <option value="OTC">OTC</option>
                <option value="SNP">SNP</option>
                <option value="WCB">WCB</option>
              </select>
            </div>
          </div>

          <div class="chartDv">
            <div class="winLossExchangeMain" id="winLossExchangeContainer"></div>
          </div>
        </div>
      </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const exchangeDropdown = this.shadowRoot.querySelector('.exchange') as HTMLSelectElement;

    if (exchangeDropdown) {
      exchangeDropdown.addEventListener('change', (e) => {
        const value = (e.target as HTMLSelectElement).value;
        this.config.exchange = value;
        this.fetchExchangeData();
      });
    }
  }

  private async fetchExchangeData(): Promise<void> {
    this.isLoading = true;
    this.setLoadingState(true);
    try {
      const moverType = (this.config.mover_type as string) || 'MostActive';
      const interval = (this.config.interval as string) || '1d';
      const exchange = (this.config.exchange as string) || 'all';

      const url = `${APIURL}/v1/discover/winner-losers-by-exchange?moverType=${encodeURIComponent(moverType)}&interval=${encodeURIComponent(interval)}&exchange=${encodeURIComponent(exchange)}`;

      const response = await apiGet({
        url,
        method: 'GET',
      });

      if (!response.ok) throw new Error(`API Error: ${response.status}`);
      const result = await response.json();
      this.exchangeData = result?.data || [];

      console.log('Exchange Data:', this.exchangeData);
      this.updateDisplay();
      this.emit('dataUpdate', this.exchangeData);
    } catch (err) {
      console.error('Error fetching exchange data:', err);
      this.exchangeData = [];
      this.updateDisplay();
    } finally {
      this.isLoading = false;
      this.setLoadingState(false);
    }
  }

  private setLoadingState(isLoading: boolean): void {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) {
      if (isLoading) {
        widget.classList.add('loading');
      } else {
        widget.classList.remove('loading');
      }
    }
  }

  private updateDisplay(): void {
    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      const container = this.shadowRoot.querySelector('#winLossExchangeContainer') as HTMLElement;
    
    if (!this.exchangeData || !Array.isArray(this.exchangeData) || this.exchangeData.length === 0) {
      container?.removeAttribute('data-testing-id');
      if (container) {
        container.innerHTML = getNoDataHTML();
      }
      return;
    }
    container?.setAttribute('data-testing-id', 'win-loss-exchange');

    const exchangesHtml = this.exchangeData.map((item: any) => {
      const exchangeName = item.exchange || 'Unknown';
      const winners = item.winners || 0;
      const losers = item.losers || 0;
      const netDifference = item.difference || (winners - losers);
      const maxValue = Math.max(winners, losers) || 1;

      const winnersPercentage = (winners / maxValue) * 100;
      const losersPercentage = (losers / maxValue) * 100;

      return `
        <div class="item">
          <h4>${exchangeName}</h4>
          <div class="rateRow winner">
            <div class="txtRow">
              <p>
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.71429 0V0.888889H10.5369L6.85714 4.70489L5.01729 2.79689C4.93692 2.71357 4.82793 2.66676 4.71429 2.66676C4.60064 2.66676 4.49165 2.71357 4.41129 2.79689L0 7.37156L0.606 8L4.71429 3.73956L6.55414 5.64756C6.63451 5.73088 6.7435 5.77768 6.85714 5.77768C6.97078 5.77768 7.07977 5.73088 7.16014 5.64756L11.1429 1.51733V4.44444H12V0H7.71429Z" fill="currentColor"/>
                </svg> Winner
              </p>
              <p>+${winners}</p>
            </div>
            <div class="barRow" style="--width: ${winnersPercentage}%;"><span></span></div>
          </div>
          <div class="rateRow loser">
            <div class="txtRow">
              <p>
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.71429 0V0.888889H10.5369L6.85714 4.70489L5.01729 2.79689C4.93692 2.71357 4.82793 2.66676 4.71429 2.66676C4.60064 2.66676 4.49165 2.71357 4.41129 2.79689L0 7.37156L0.606 8L4.71429 3.73956L6.55414 5.64756C6.63451 5.73088 6.7435 5.77768 6.85714 5.77768C6.97078 5.77768 7.07977 5.73088 7.16014 5.64756L11.1429 1.51733V4.44444H12V0H7.71429Z" fill="currentColor"/>
                </svg> Loser
              </p>
              <p>-${losers}</p>
            </div>
            <div class="barRow" style="--width: ${losersPercentage}%;"><span></span></div>
          </div>
          <div class="resultRow ${netDifference >= 0 ? 'winner' : 'loser'}">
            <p>Net Difference</p>
            <p>
              <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.71429 0V0.888889H10.5369L6.85714 4.70489L5.01729 2.79689C4.93692 2.71357 4.82793 2.66676 4.71429 2.66676C4.60064 2.66676 4.49165 2.71357 4.41129 2.79689L0 7.37156L0.606 8L4.71429 3.73956L6.55414 5.64756C6.63451 5.73088 6.7435 5.77768 6.85714 5.77768C6.97078 5.77768 7.07977 5.73088 7.16014 5.64756L11.1429 1.51733V4.44444H12V0H7.71429Z" fill="currentColor"/>
              </svg> ${netDifference >= 0 ? '+' : ''}${netDifference}
            </p>
          </div>
        </div>
      `;
    }).join('');
    if (container) {
      container.innerHTML = exchangesHtml;
    }
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }
  
  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }
  
  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }
  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    this.updateDisplay();
  }
  
  public destroy(): void {
    if (this.widgetResizeObserver) { 
      this.widgetResizeObserver.disconnect(); 
      this.widgetResizeObserver = null; 
    }
    this.eventHandlers.clear(); 
    this.shadowRoot.innerHTML = '';
  }
  
  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { 
      const evt: WidgetEvent = { type, data }; 
      handlers.forEach(h => h(evt)); 
    }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
  }

}

declare global { 
  interface Window { 
    TraderverseWinnersLosersExchange: typeof TraderverseWinnersLosersExchange; 
  } 
}
if (typeof window !== 'undefined') { 
  window.TraderverseWinnersLosersExchange = TraderverseWinnersLosersExchange; 
}